/**
 * Client-side page preview for the editor: the browser twin of sitebuilder/render/page.go (RenderPage) built on the
 * LiquidJS engine of sitebuilder/js/src/preview.js (copied as ./previewEngine.js). Differences from the gateway, all
 * on purpose: no HTML sanitizer pass (the preview runs in a sandboxed iframe without same-origin), no runtime /
 * behaviors / component scripts, sample platform data (product, collection …), and editor markers
 * (data-ed-id on every instance, data-ed-slot drop zones). "Accurate preview" renders through the server.
 */
import { SafeHTML, normalize, renderRaw, setRichtextSanitizer } from './previewEngine';
import { iconSVG } from './icons';
import { cleanHTML } from '../RichEditor';
import { richtextSanitizer } from './sanitize';

// Same serializer as the gateway twin (sanitize.js); DOMPurify's role is played by the editor's own cleaner
// (same tag/attribute policy) so the console needs no extra dependency.
const purifier = {
    sanitize: (html) => {
        const tpl = document.createElement('template');
        tpl.innerHTML = cleanHTML(html, false);
        return tpl.content;
    },
};
setRichtextSanitizer(richtextSanitizer(purifier));

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// ---- theme + base CSS (same as render/head.go themeCSS + baseCSS) ----
export const FONT_STACKS = {
    system: 'system-ui,-apple-system,"Segoe UI",Roboto,"Noto Sans","PingFang SC","Microsoft YaHei",sans-serif',
    serif: 'Georgia,"Times New Roman","Noto Serif","Songti SC",serif',
    rounded: 'ui-rounded,"SF Pro Rounded","Nunito",system-ui,sans-serif',
    mono: 'ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
};
export const DEFAULT_THEME = { accent: '#2357ff', background: '#ffffff', text: '#111827', font: 'system', radius: 12 };
const reHex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const themeCSS = (t = {}) => {
    const c = (v, d) => (v && reHex.test(v) ? v : d);
    const r = Number.isFinite(Number(t.radius)) && t.radius >= 0 && t.radius <= 40 ? Number(t.radius) : 12;
    return `:root{--yc-accent:${c(t.accent, '#2357ff')};--yc-bg:${c(t.background, '#ffffff')};--yc-text:${c(t.text, '#111827')};--yc-font:${FONT_STACKS[t.font] || FONT_STACKS.system};--yc-radius:${r}px}`;
};

const BASE_CSS = '*,*::before,*::after{box-sizing:border-box}body{margin:0;font-family:var(--yc-font);color:var(--yc-text);background:var(--yc-bg);line-height:1.6;-webkit-font-smoothing:antialiased}img,video{max-width:100%;height:auto;display:block}a{color:var(--yc-accent)}h1,h2,h3,h4{line-height:1.2;margin:0 0 .5em}p{margin:0 0 1em}.yc-icon{display:inline-block;vertical-align:middle;width:1.25em;height:1.25em}[data-yc-c]{display:contents}'
    + '[data-yc-c].yc-styled{display:block}'
    + '[data-yc=reveal]{opacity:1}[data-yc=marquee]{display:flex;overflow:hidden}[data-yc=marquee]>*{flex:none}'
    + '[data-yc=carousel] [data-track]{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none}[data-yc=carousel] [data-track]>*{flex:0 0 100%;scroll-snap-align:start}'
    + '[data-modal]{position:fixed;inset:0;background:rgba(0,0,0,.5);display:grid;place-items:center;z-index:9000}[data-modal][hidden]{display:none}';

// ---- CSS scoping (same rules as render/css.go ScopeCSS) using the browser's CSS parser ----
const scopedCache = new Map();
const reRootSel = /^(:root|html|body)\b/;
const splitSelectors = (s) => {
    const out = [];
    let depth = 0;
    let start = 0;
    for (let i = 0; i < s.length; i++) {
        const ch = s[i];
        if (ch === '(' || ch === '[') depth++;
        else if (ch === ')' || ch === ']') depth--;
        else if (ch === ',' && depth === 0) { out.push(s.slice(start, i)); start = i + 1; }
    }
    out.push(s.slice(start));
    return out;
};
const scopeSelector = (sel, scope) => {
    sel = sel.trim();
    if (reRootSel.test(sel)) return scope + sel.replace(reRootSel, '');
    if (sel.startsWith('&')) return scope + sel.slice(1);
    return `${scope} ${sel}`;
};

export const scopeCSS = (css, name) => {
    if (!css || !css.trim()) return '';
    const key = `${name}\u0000${css}`;
    if (scopedCache.has(key)) return scopedCache.get(key);
    const scope = `.ycc-${String(name).toLowerCase()}`;
    const framePrefix = `ycc-${String(name).toLowerCase()}-`;
    let out = '';
    try {
        if (/expression\s*\(|javascript:|behavior\s*:|-moz-binding|@import/i.test(css)) throw new Error('forbidden');
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(css);
        const frames = {};
        const walk = (rules) => {
            let s = '';
            for (const r of rules) {
                if (r.type === 1) { // style rule
                    s += `${splitSelectors(r.selectorText).map((x) => scopeSelector(x, scope)).join(',')}{${r.style.cssText}}`;
                } else if (r.type === 4) { // media
                    s += `@media ${r.conditionText || r.media.mediaText}{${walk(r.cssRules)}}`;
                } else if (r.type === 12) { // supports
                    s += `@supports ${r.conditionText}{${walk(r.cssRules)}}`;
                } else if (r.type === 7) { // keyframes
                    frames[r.name] = framePrefix + r.name;
                    s += r.cssText.replace(/^@keyframes\s+\S+/, `@keyframes ${framePrefix}${r.name}`);
                } else if (r.constructor?.name === 'CSSContainerRule') {
                    s += `@container ${r.conditionText}{${walk(r.cssRules)}}`;
                }
            }
            return s;
        };
        out = walk(sheet.cssRules);
        Object.entries(frames).forEach(([orig, renamed]) => {
            out = out.replace(/(animation(?:-name)?:)([^;}]*)/g, (m, p, v) => p + v.replace(new RegExp(`\\b${orig.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')}\\b`, 'g'), renamed));
        });
    } catch (_) {
        out = '';
    }
    scopedCache.set(key, out);
    return out;
};

// ---- sample platform data (validate/contexts.go SampleData), images as inline SVG placeholders ----
const ph = (label, w = 1200, h = 1200) => ({
    src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}"><rect width="100%" height="100%" fill="#e5e7eb"/><text x="50%" y="50%" fill="#9ca3af" font-family="sans-serif" font-size="${Math.round(w / 12)}" text-anchor="middle" dominant-baseline="middle">${label}</text></svg>`)}`,
    alt: label, width: w, height: h,
});
const SafeHTMLLike = (html) => new SafeHTML(html);
let SAMPLE = null;
export const sampleData = (currency = 'USD', shopName = 'Shop') => {
    if (SAMPLE && SAMPLE.shop.currency === currency && SAMPLE.shop.name === shopName) return SAMPLE;
    const variant = (id, title, price, avail) => ({ id, title, sku: `SKU-${title}`, price, compare_at_price: null, available: avail, options: [title], image: null, inventory_quantity: 5 });
    const product = (handle, title, price, n) => ({
        id: n, handle, title, url: `/products/${handle}`, vendor: 'Acme', type: 'Shirts',
        description: SafeHTMLLike('<p>A soft cotton <strong>shirt</strong>.</p>'), tags: ['cotton', 'summer'], available: true,
        price, compare_at_price: price + 1000, price_min: price, price_max: price + 500, currency,
        images: [ph(title), ph(`${title} 2`)], featured_image: ph(title),
        options: [{ name: 'Size', values: ['S', 'M', 'L'] }],
        variants: [variant(11, 'S', price, true), variant(12, 'M', price, true), variant(13, 'L', price + 500, false)],
        seo: { title: '', description: '' }, metafields: { custom: { subtitle: 'Soft everyday cotton' } },
    });
    const p1 = product('blue-shirt', 'Blue shirt', 2500, 1);
    const p2 = product('red-shirt', 'Red shirt', 2900, 2);
    const p3 = product('green-shirt', 'Green shirt', 3100, 3);
    const pg = { page: 1, pages: 3, prev: false, next: true, prev_page: 0, next_page: 2 };
    const post = {
        slug: 'hello-world', title: 'Hello world', url: '/blog/hello-world', excerpt: 'Our first post.',
        body: SafeHTMLLike('<h2>Intro</h2><p>Welcome to our blog.</p>'), cover: ph('Cover', 1200, 630), author: 'Ann',
        tags: ['news'], published_at: '2026-11-01T08:00:00Z', updated_at: '2026-11-01T09:00:00Z',
    };
    const cmsItem = (slug, title) => ({
        type: 'sample', slug, title, url: `/sample/${slug}`, excerpt: 'A short summary.', image: ph(title), published_at: '2026-11-01T08:00:00Z', sort: 0, jsonld: null, data: { title, summary: 'A short summary.' },
        display: [{ name: 'summary', label: 'Summary', type: 'text', text: 'A short summary.', html: '', image: null, href: '', items: [] }, { name: 'body', label: 'Details', type: 'richtext', text: '', html: SafeHTMLLike('<p>Details.</p>'), image: null, href: '', items: [] }],
    });
    const cmsSample = {};
    const cmsTypes = ['team', 'services', 'work', 'jobs', 'events', 'locations', 'testimonials', 'faq'].map((slug) => {
        const items = [cmsItem('first', `First: ${slug}`), cmsItem('second', `Second: ${slug}`), cmsItem('third', `Third: ${slug}`)];
        cmsSample[slug] = items;
        return { slug, name: slug, items };
    });
    SAMPLE = {
        item: cmsItem('first', 'Sample item'),
        cms: { ...cmsSample, types: cmsTypes },
        shop: { name: shopName, currency, cart_url: '/cart', account_url: '/account', search_url: '/search', products_count: 3, collections: [{ handle: 'shirts', title: 'Shirts', url: '/collections/shirts' }] },
        product: p1,
        collection: { handle: 'shirts', title: 'Shirts', url: '/collections/shirts', description: SafeHTMLLike('<p>All shirts.</p>'), image: ph('Shirts', 1600, 600), products: [p1, p2, p3], products_count: 3, pagination: pg, metafields: { custom: { subtitle: 'Soft everyday cotton' } } },
        post,
        blog: { title: 'Blog', url: '/blog', posts: [post, { ...post, slug: 'second', title: 'Second post', url: '/blog/second' }], posts_count: 2, tags: ['news'], tag: '', pagination: pg },
        search: { query: 'shirt', count: 1, results: [{ kind: 'product', title: 'Blue shirt', url: '/products/blue-shirt', image: ph('Blue shirt'), price: 2500, currency, excerpt: '' }] },
    };
    return SAMPLE;
};

// validate/metafields.go SampleDataFor: merchants define their own keys, so a key the template names gets a sample value
const reMetafieldRef = /\.metafields\.([a-z0-9_]{1,64})\.([a-z0-9_]{1,64})/g;
export const addMetafieldSamples = (data, template) => {
    const refs = [...String(template || '').matchAll(reMetafieldRef)];
    if (!refs.length || !data) return;
    const add = (o) => {
        if (!o || typeof o !== 'object') return;
        const mf = o.metafields || (o.metafields = {});
        refs.forEach(([, ns, key]) => {
            mf[ns] = mf[ns] || {};
            if (!(key in mf[ns])) mf[ns][key] = 'Sample value';
        });
    };
    add(data.product);
    add(data.collection);
    (data.collection?.products || []).forEach(add);
};

// site.menus for templates (render/page.go menuVars): header + footer always present, one level of children
const reMenuURL = /^(\/[^/]|\/$|https?:\/\/|mailto:|tel:)/;
const reMenuKey = /^[a-z][a-z0-9_]{0,20}$/;
export const menuVars = (m) => {
    const conv = (items, depth) => (Array.isArray(items) ? items : []).slice(0, 30).map((it) => {
        let u = String(it?.url || '').trim();
        if (!reMenuURL.test(u)) u = '/';
        return { label: String(it?.label || ''), url: u, children: depth === 0 ? conv(it?.children, 1) : [] };
    });
    const out = { header: [], footer: [] };
    Object.entries(m || {}).forEach(([k, items]) => { if (reMenuKey.test(k)) out[k] = conv(items, 0); });
    return out;
};

// ---- inline editing markers (editor only; published HTML and the Go/JS conformance never see them) ----
// Non-empty text/textarea values get private-use sentinels around them before rendering, then the output is walked:
// a sentinel pair in text content becomes <span data-yc-field="path">, anywhere else (attributes, script, url_encode…) it
// is removed. A span is only made when the output is exactly the value (a truncate/upcase result can't be edited back),
// and an instance whose output differs from the clean render (filters like size) keeps the clean render.
const OPEN = '\uE000'; const MID = '\uE001'; const CLOSE = '\uE002';
const reStrip = /\uE000\d+\uE001|\uE002|%EE%80%80\d+%EE%80%81|%EE%80%82/g;
const stripMarks = (h) => h.replace(reStrip, '');
const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
const RAW = new Set(['script', 'style', 'textarea', 'title', 'option', 'select']);

const markProps = (fields, values, prefix, table) => {
    const out = { ...values };
    Object.entries(fields || {}).forEach(([k, f]) => {
        const v = values?.[k];
        const path = prefix ? `${prefix}.${k}` : k;
        if ((f.type === 'text' || f.type === 'textarea') && typeof v === 'string' && v !== '') {
            table.push({ path, value: v, multi: f.type === 'textarea' });
            out[k] = `${OPEN}${table.length - 1}${MID}${v}${CLOSE}`;
        } else if (f.type === 'list' && Array.isArray(v)) {
            out[k] = v.map((it, i) => markProps(f.item, it, `${path}.${i}`, table));
        } else if (f.type === 'object' && v && typeof v === 'object') {
            out[k] = markProps(f.fields, v, path, table);
        }
    });
    return out;
};

/** escapeHTML of the preview engine: the bytes a plain {{ value }} produces */
const outputOf = (v) => String(v).replaceAll('&', '&amp;').replaceAll("'", '&#39;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&#34;');

const applyMarks = (html, table) => {
    let out = '';
    let i = 0;
    let raw = null; // inside <script>… and the like
    let open = null; // { at: index in out, n: marker, depth, bad }
    let depth = 0;
    const n = html.length;
    while (i < n) {
        const ch = html[i];
        if (ch === '<') {
            let j = i + 1;
            let q = null;
            while (j < n && (q || html[j] !== '>')) {
                if (q) { if (html[j] === q) q = null; } else if (html[j] === '"' || html[j] === "'") q = html[j];
                j++;
            }
            const tag = html.slice(i, j + 1);
            const m = tag.match(/^<(\/?)([a-zA-Z][a-zA-Z0-9-]*)/);
            if (m) {
                const name = m[2].toLowerCase();
                if (raw) { if (m[1] && name === raw) raw = null; } else if (RAW.has(name) && !m[1] && !tag.endsWith('/>')) raw = name;
                else if (!VOID.has(name) && !tag.endsWith('/>')) {
                    depth += m[1] ? -1 : 1;
                    if (open && depth !== open.depth) open.bad = true;
                }
            }
            out += stripMarks(tag);
            i = j + 1;
        } else if (ch === OPEN) {
            const mid = html.indexOf(MID, i);
            const idx = Number(html.slice(i + 1, mid));
            if (!raw && !open) open = { at: out.length, n: idx, depth, bad: false };
            i = mid + 1;
        } else if (ch === CLOSE) {
            if (open && !raw) {
                const f = table[open.n];
                const inner = out.slice(open.at);
                const exact = outputOf(f?.value ?? '');
                if (f && !open.bad && depth === open.depth && (inner === exact || inner === exact.replaceAll('\n', '<br>'))) {
                    out = `${out.slice(0, open.at)}<span data-yc-field="${esc(f.path)}"${f.multi ? ' data-yc-multi="1"' : ''}>${inner}</span>`;
                }
            }
            open = null;
            i += 1;
        } else {
            out += ch;
            i += 1;
        }
    }
    return out;
};

/** wrapper classes + style of an instance (`_style`): same output as render/page.go withInstanceStyle */
const instanceStyle = (st, editor) => {
    if (!st || typeof st !== 'object') return { classes: '', style: '' };
    const hide = Array.isArray(st.hide_on) ? st.hide_on : [];
    let classes = '';
    [['mobile', 'm'], ['tablet', 't'], ['desktop', 'd']].forEach(([d, c]) => { if (hide.includes(d)) classes += ` ${editor ? 'yc-ed-hide-' : 'yc-hide-'}${c}`; });
    const decl = [];
    const pad = (prop, v) => { if (Number.isFinite(v)) decl.push(`${prop}:${Math.min(Math.max(Math.trunc(v), 0), 400)}px`); };
    pad('padding-top', st.padding_top);
    pad('padding-bottom', st.padding_bottom);
    if (typeof st.background === 'string' && reHex.test(st.background)) decl.push(`background:${st.background.toLowerCase()}`);
    if (decl.length) classes += ' yc-styled';
    return { classes, style: decl.join(';') };
};

// ---- page render ----
const MAX_DEPTH = 6;
const reIcon = /<i data-yc-icon="([a-z0-9-]+)"><\/i>/g;
const reBehavior = /data-yc="([a-z-]+)"/g;

/** registry: (type, version) → definition | null; missing: (type, version) → void (to fetch it) */
export async function renderPage({ page, site, theme, getDef, data, editor = true, labels = {}, regions = null }) {
    const problems = [];
    const css = new Map();
    const ctx = {
        site: { name: site?.name || '', lang: page?.lang || site?.default_lang || 'en', url: site?.url || '', logo: site?.settings?.logo || '', menus: menuVars(site?.settings?.menus) },
        page: { title: page?.title || '', path: page?.path || '/', type: page?.type || 'page' },
        data,
    };

    const renderList = async (list, depth, path, slotKey, editor) => {
        let out = '';
        if (depth > MAX_DEPTH) { problems.push({ path, message: 'nested deeper than allowed' }); return ''; }
        for (let i = 0; i < (list || []).length; i++) {
            const inst = list[i];
            const p = `${path}[${i}]`;
            const def = getDef(inst.type, inst.version);
            if (!def) {
                out += editor ? `<div data-ed-id="${esc(inst.id)}" class="yc-ed-missing">${esc(inst.type)}</div>` : '';
                continue;
            }
            const slots = {};
            for (const [name, f] of Object.entries(def.fields || {})) {
                if (f.type !== 'slot') continue;
                let children = inst.slots?.[name] || [];
                if (f.maxChildren > 0) children = children.slice(0, f.maxChildren);
                if (f.allow?.length) children = children.filter((c) => f.allow.includes(c.type));
                const inner = await renderList(children, depth + 1, `${p}.slots.${name}`, `${inst.id}:${name}`, editor);
                slots[name] = editor
                    ? (children.length
                        ? `<div data-ed-slot="${esc(inst.id)}:${esc(name)}" style="display:contents">${inner}</div>`
                        : `<div data-ed-slot="${esc(inst.id)}:${esc(name)}" class="yc-ed-empty">${esc(labels.dropHere || '+')} · ${esc(f.label || name)}</div>`)
                    : inner;
            }
            let html;
            try {
                const { props, problems: pr } = normalize(def, inst.props || {}, slots);
                pr.forEach((x) => problems.push({ id: inst.id, path: `${p}.${x.path}`, message: x.message }));
                addMetafieldSamples(ctx.data, def.template);
                html = await renderRaw(def, props, ctx);
                if (editor) {
                    const table = [];
                    const marked = markProps(def.fields, props, '', table);
                    if (table.length) {
                        const withMarks = await renderRaw(def, marked, ctx);
                        if (stripMarks(withMarks) === html) html = applyMarks(withMarks, table);
                    }
                }
            } catch (e) {
                problems.push({ id: inst.id, path: p, message: String(e?.message || e) });
                out += editor ? `<div data-ed-id="${esc(inst.id)}" class="yc-ed-missing yc-ed-error">${esc(def.label || def.name)}: ${esc(e?.message || e)}</div>` : '';
                continue;
            }
            const declared = new Set(def.behaviors || []);
            html = html.replace(reIcon, (m, name) => iconSVG(name))
                .replace(reBehavior, (m, b) => (declared.has(b) ? m : `data-yc-dropped="${b}"`));
            const scope = `ycc-${String(def.name).toLowerCase()}`;
            if (!css.has(scope)) css.set(scope, scopeCSS(def.css, def.name));
            const st = instanceStyle(inst._style, editor);
            out += `<div class="${scope}${st.classes}" data-yc-c="${esc(def.name)}"${st.style ? ` style="${st.style}"` : ''}${editor ? ` data-ed-id="${esc(inst.id)}"` : ''}>${html}</div>`;
        }
        void slotKey;
        return out;
    };

    const activeBody = async () => {
        const b = await renderList(page?.content || [], 0, 'content', 'root', editor);
        return editor && !(page?.content || []).length ? `<div data-ed-slot="root" class="yc-ed-empty yc-ed-root">${esc(labels.emptyPage || '')}</div>` : b;
    };
    let body = '';
    if (regions) {
        // global layout: the region being edited is live, the others are shown locked; click one to switch to it
        for (const r of regions) {
            if (r.active) body += await activeBody();
            else if (r.content?.length) body += `<div data-ed-lock="${esc(r.key)}" style="display:contents">${await renderList(r.content, 0, r.key, 'root', false)}</div>`;
            else if (r.placeholder) body += `<div data-ed-lock="${esc(r.key)}" class="yc-ed-lock-empty">${esc(r.placeholder)}</div>`;
        }
    } else body = await activeBody();
    return { body, css: themeCSS(theme || DEFAULT_THEME) + BASE_CSS + [...css.values()].join(''), problems };
}

// ---- iframe document with the editor bridge (selection, hover, drag and drop → postMessage) ----
const EDITOR_CSS = `
.yc-ed-empty{min-height:72px;margin:8px;border:2px dashed #c3c8d4;border-radius:8px;display:flex;align-items:center;justify-content:center;color:#8a93a6;font:13px system-ui,sans-serif;background:rgba(240,243,250,.6)}
.yc-ed-root{min-height:60vh;margin:24px}
.yc-ed-missing{padding:16px;margin:8px;border:1px dashed #f0a020;color:#a06000;font:13px system-ui,sans-serif;background:#fff8e6}
.yc-ed-error{border-color:#e5484d;color:#b42318;background:#fff0f0}
#yc-ed-hover,#yc-ed-sel{position:absolute;pointer-events:none;z-index:2147483600;border:1px solid #4f7cff;display:none}
#yc-ed-sel{border:2px solid #2357ff}
#yc-ed-tag{position:absolute;z-index:2147483601;display:none;align-items:center;gap:1px;background:#2357ff;color:#fff;font:600 12px/1 system-ui,sans-serif;padding:2px 3px;border-radius:5px 5px 0 0;white-space:nowrap;user-select:none;transform-origin:0 0}
#yc-ed-tag .h{padding:4px 6px;cursor:grab}
#yc-ed-tag button{all:unset;width:22px;height:22px;display:inline-grid;place-items:center;border-radius:4px;cursor:pointer;font:13px/1 system-ui,sans-serif;color:#fff}
#yc-ed-tag button:hover{background:rgba(255,255,255,.25)}
#yc-ed-tag.lock{background:#7c3aed}
#yc-ed-hover.lock{border:2px dashed #7c3aed;background:rgba(124,58,237,.05)}
[data-yc-field]{cursor:text}
[data-yc-field]:hover{outline:1px dashed rgba(35,87,255,.7);outline-offset:2px}
[data-yc-field][contenteditable]{outline:2px solid #2357ff;outline-offset:2px;cursor:text;user-select:text;-webkit-user-select:text}
.yc-ed-lock-empty{display:block;margin:8px;padding:10px;border:1px dashed #b8a4ee;border-radius:6px;color:#7c3aed;text-align:center;font:12px system-ui,sans-serif}
@media(max-width:767px){[data-yc-c].yc-ed-hide-m{display:block;opacity:.35;outline:1px dashed #9ca3af;outline-offset:-1px}}
@media(min-width:768px) and (max-width:1023px){[data-yc-c].yc-ed-hide-t{display:block;opacity:.35;outline:1px dashed #9ca3af;outline-offset:-1px}}
@media(min-width:1024px){[data-yc-c].yc-ed-hide-d{display:block;opacity:.35;outline:1px dashed #9ca3af;outline-offset:-1px}}
#yc-ed-line{position:absolute;pointer-events:none;z-index:2147483602;height:4px;background:#ff7a00;border-radius:2px;display:none;box-shadow:0 0 0 2px rgba(255,255,255,.8)}
.yc-ed-target{outline:2px solid #ff7a00!important;outline-offset:-2px;background:rgba(255,122,0,.06)!important}
`;

const BRIDGE = `(()=>{
const send=(m)=>parent.postMessage(Object.assign({__yced:1},m),'*');
const $=(id)=>document.getElementById(id);
let lastBody=null,lastCss=null,sel=null,hov=null,lock=null,labels={},ui={},scale=1,dragging=false,barId=null,editing=null;
const box=(el)=>{if(!el)return null;const r=el.getBoundingClientRect();if(r.width||r.height)return r;let x1=1e9,y1=1e9,x2=-1e9,y2=-1e9,f=false;for(const c of el.children){const b=box(c);if(b&&(b.width||b.height)){f=true;x1=Math.min(x1,b.left);y1=Math.min(y1,b.top);x2=Math.max(x2,b.right);y2=Math.max(y2,b.bottom)}}return f?{left:x1,top:y1,right:x2,bottom:y2,width:x2-x1,height:y2-y1}:null};
const place=(div,el)=>{const b=box(el);if(!b){div.style.display='none';return null}div.style.display='block';div.style.left=(b.left+scrollX)+'px';div.style.top=(b.top+scrollY)+'px';div.style.width=b.width+'px';div.style.height=b.height+'px';return b};
const byId=(id)=>id?document.querySelector('[data-ed-id="'+CSS.escape(id)+'"]'):null;
const ACTS=[['up','↑'],['down','↓'],['dup','⧉'],['ai','✦'],['save','☆'],['del','✕']];
const tag=$('yc-ed-tag');
tag.innerHTML='<span class="h" draggable="true"></span>'+ACTS.map((a)=>'<button type="button" data-a="'+a[0]+'">'+a[1]+'</button>').join('');
const head=tag.querySelector('.h');
const showBar=(b,text,isLock)=>{tag.style.display='flex';tag.classList.toggle('lock',!!isLock);head.textContent=text;tag.querySelectorAll('button').forEach((x)=>{x.style.display=isLock?'none':'';x.title=ui[x.dataset.a]||''});tag.style.transform='scale('+(1/scale)+')';const w=tag.offsetWidth/scale,h=tag.offsetHeight/scale;tag.style.left=Math.max(scrollX,Math.min(b.left+scrollX,scrollX+innerWidth-w))+'px';tag.style.top=Math.max(scrollY,b.top+scrollY-h)+'px'};
const draw=()=>{const hv=$('yc-ed-hover');const sb=place($('yc-ed-sel'),byId(sel));hv.classList.toggle('lock',!!lock);const h=lock||(hov&&hov!==sel?byId(hov):null);const hb=h?place(hv,h):(hv.style.display='none',null);tag.style.display='none';barId=null;if(dragging||editing)return;if(lock&&hb){showBar(hb,labels['__'+lock.getAttribute('data-ed-lock')]||'',true);return}const id=hov||sel;const b=hov&&hov!==sel?hb:sb;if(id&&b){barId=id;showBar(b,'⠿ '+(labels[id]||''),false)}};
addEventListener('scroll',draw,{passive:true});addEventListener('resize',draw);
document.addEventListener('mousemove',(e)=>{if(dragging||editing)return;const t=e.target;if(!t.closest||t.closest('#yc-ed-tag'))return;const l=t.closest('[data-ed-lock]');const el=l?null:t.closest('[data-ed-id]');const id=el?el.getAttribute('data-ed-id'):null;if(id!==hov||l!==lock){hov=id;lock=l;draw()}});
document.addEventListener('mouseleave',()=>{hov=null;lock=null;draw()});
const finish=(save)=>{const el=editing;if(!el)return;editing=null;const before=el.__ycOrig;const multi=el.hasAttribute('data-yc-multi');let v=el.innerText.replace(/ /g,' ').replace(/\\n$/,'');if(!multi)v=v.replace(/\\s*\\n\\s*/g,' ');el.removeAttribute('contenteditable');if(!save||v===before.text){el.innerHTML=before.html}else{const inst=el.closest('[data-ed-id]');if(inst)send({type:'inline',id:inst.getAttribute('data-ed-id'),path:el.getAttribute('data-yc-field'),value:v})}draw()};
const startEdit=(el)=>{if(editing)finish(true);editing=el;el.__ycOrig={html:el.innerHTML,text:el.innerText};try{el.contentEditable='plaintext-only'}catch(_){el.contentEditable='true'}el.focus();const r=document.createRange();r.selectNodeContents(el);const s=getSelection();s.removeAllRanges();s.addRange(r);el.addEventListener('blur',()=>finish(true),{once:true});draw()};
document.addEventListener('paste',(e)=>{if(!editing||editing.contentEditable==='plaintext-only')return;e.preventDefault();document.execCommand('insertText',false,e.clipboardData.getData('text/plain'))});
document.addEventListener('dblclick',(e)=>{if(editing&&editing.contains(e.target))return;e.preventDefault();const t=e.target.closest&&e.target.closest('[data-yc-field]');if(t&&!t.closest('[data-ed-lock]'))startEdit(t)},true);
document.addEventListener('click',(e)=>{if(editing&&editing.contains(e.target))return;e.preventDefault();e.stopPropagation();const inBar=e.target.closest&&e.target.closest('#yc-ed-tag');if(inBar){const b=e.target.closest('button');if(b&&barId)send({type:'action',action:b.dataset.a,id:barId});return}const l=e.target.closest&&e.target.closest('[data-ed-lock]');if(l){send({type:'region',key:l.getAttribute('data-ed-lock')});return}const el=e.target.closest&&e.target.closest('[data-ed-id]');const slot=e.target.closest&&e.target.closest('.yc-ed-empty');if(slot&&!el){send({type:'slotclick',slot:slot.getAttribute('data-ed-slot')});return}sel=el?el.getAttribute('data-ed-id'):null;draw();send({type:'select',id:sel})},true);
document.addEventListener('submit',(e)=>e.preventDefault(),true);
document.addEventListener('keydown',(e)=>{const k=e.key;if(editing){e.stopPropagation();if(k==='Escape'){e.preventDefault();finish(false)}else if(k==='Enter'&&!editing.hasAttribute('data-yc-multi')){e.preventDefault();finish(true)}return}if((e.ctrlKey||e.metaKey)||k==='Delete'||k==='Backspace'||k==='Escape'){e.preventDefault();send({type:'key',key:k,ctrl:e.ctrlKey||e.metaKey,shift:e.shiftKey})}});
head.addEventListener('dragstart',(e)=>{if(!barId)return;e.dataTransfer.effectAllowed='move';try{e.dataTransfer.setData('text/plain','yc-move:'+barId)}catch(_){}const id=barId;dragging=true;send({type:'dragstart',id})});
head.addEventListener('dragend',()=>{dragging=false;clear();send({type:'dragend'})});
let tgtEl=null;
const clear=()=>{$('yc-ed-line').style.display='none';if(tgtEl){tgtEl.classList.remove('yc-ed-target');tgtEl=null}};
// Drop target under (x,y): an empty slot, before/after the block under the pointer, else the nearest top-level
// position (gaps, locked global regions). Drags started in the console never reach this opaque-origin frame (Chrome
// drops drag events across origins), so the console lays a catcher over the canvas and relays the pointer as dragpos.
const tops=()=>Array.from(document.querySelectorAll('[data-ed-id]')).filter((x)=>!x.closest('[data-ed-lock]')&&!(x.parentElement&&x.parentElement.closest('[data-ed-id]')));
const target=(t,y)=>{if(!t||!t.closest)return null;const empty=t.closest('.yc-ed-empty');if(empty&&!empty.closest('[data-ed-lock]'))return{slot:empty.getAttribute('data-ed-slot'),el:empty};const el=t.closest('[data-ed-lock]')?null:t.closest('[data-ed-id]');if(el){const b=box(el);const where=b&&y>b.top+b.height/2?'after':'before';return{id:el.getAttribute('data-ed-id'),where,el,b}}const list=tops();if(!list.length){const r=document.querySelector('.yc-ed-root');return r?{slot:'root',el:r}:{slot:'root',where:'end'}}for(const x of list){const b=box(x);if(b&&y<b.top+b.height/2)return{id:x.getAttribute('data-ed-id'),where:'before',el:x,b}}const x=list[list.length-1];return{id:x.getAttribute('data-ed-id'),where:'after',el:x,b:box(x)}};
const show=(tg)=>{clear();if(!tg)return;if(tg.slot&&tg.el){tgtEl=tg.el;tgtEl.classList.add('yc-ed-target')}else if(tg.b){const l=$('yc-ed-line');l.style.display='block';l.style.left=(tg.b.left+scrollX)+'px';l.style.width=Math.max(tg.b.width,40)+'px';l.style.top=((tg.where==='after'?tg.b.bottom:tg.b.top)+scrollY-2)+'px'}};
const EDGE=56;const autoScroll=(y)=>{const d=y<EDGE?-(EDGE-y):y>innerHeight-EDGE?y-(innerHeight-EDGE):0;if(d)scrollBy(0,Math.round(d/2)+(d>0?4:-4))};
const at=(x,y)=>{tag.style.display='none';return target(document.elementFromPoint(Math.max(0,Math.min(x,innerWidth-1)),Math.max(0,Math.min(y,innerHeight-1))),y)};
const dropMsg=(tg)=>tg?{type:'drop',id:tg.id||null,where:tg.where||null,slot:tg.slot||null}:{type:'drop',none:true};
document.addEventListener('dragover',(e)=>{e.preventDefault();dragging=true;tag.style.display='none';autoScroll(e.clientY);show(target(e.target,e.clientY))});
document.addEventListener('dragleave',(e)=>{if(!e.relatedTarget)clear()});
document.addEventListener('drop',(e)=>{e.preventDefault();dragging=false;const tg=target(e.target,e.clientY);clear();send(dropMsg(tg))});
addEventListener('message',(e)=>{const m=e.data||{};if(m.type==='render'){if(m.css!==lastCss){lastCss=m.css;$('yc-css').textContent=m.css}if(m.body!==lastBody){lastBody=m.body;editing=null;lock=null;hov=null;document.getElementById('yc-main').innerHTML=m.body}labels=m.labels||{};ui=m.ui||ui;if(m.scale)scale=m.scale;sel=m.selected||null;requestAnimationFrame(draw);if(m.scrollTo){const el=byId(m.scrollTo);const b=box(el);if(b&&(b.top<0||b.top>innerHeight))scrollTo({top:b.top+scrollY-80,behavior:'smooth'})}}else if(m.type==='select'){sel=m.id;draw();if(m.scroll){const b=box(byId(m.id));if(b&&(b.top<0||b.bottom>innerHeight))scrollTo({top:b.top+scrollY-80,behavior:'smooth'})}}else if(m.type==='ui'){scale=m.scale||1;draw()}else if(m.type==='dragpos'){if(m.end){dragging=false;clear();draw();return}dragging=true;autoScroll(m.y);show(at(m.x,m.y))}else if(m.type==='dragdrop'){dragging=false;const tg=at(m.x,m.y);clear();draw();send(dropMsg(tg))}});
new ResizeObserver(draw).observe(document.body);
document.addEventListener('load',(e)=>{if(e.target.tagName==='IMG')draw()},true);
send({type:'ready'});
})();`;

export const frameDocument = (lang = 'en') => `<!doctype html><html lang="${esc(lang)}"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank">`
    + `<style id="yc-css"></style><style>${EDITOR_CSS}</style></head><body><main id="yc-main"></main>`
    + '<div id="yc-ed-hover"></div><div id="yc-ed-sel"></div><div id="yc-ed-tag"></div><div id="yc-ed-line"></div>'
    + `<script>${BRIDGE}</script></body></html>`;
